import {useCallback, useEffect, useRef} from 'react';

/**
 * Hook to scroll to a specific segment by index and apply highlighting.
 * Uses MutationObserver to wait for the segment element to appear in the DOM.
 *
 * @param {number|null} segmentIndex - The segment index to scroll to
 * @param {string[]} searchResultSegmentIds - Array of segment IDs to highlight as secondary
 * @param {Array} segments - Array of segment data to find matching IDs
 */
export function useScrollToSegment(segmentIndex, searchResultSegmentIds = [], segments = []) {
    const hasScrolledRef = useRef(false);
    const observerRef = useRef(null);

    // Inject CSS styles for highlighting (only once)
    useEffect(() => {
        const styleId = 'segment-highlight-styles';
        if (document.getElementById(styleId)) return;

        const styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
            .segment-primary-highlight {
                position: relative;
                outline: 2px dotted blue;
                margin-top: 36px !important;
            }
            .segment-primary-highlight::before {
                content: "Fragment you clicked";
                position: absolute;
                left: 0;
                top: 0;
                transform: translateY(-100%);
                background: blue;
                color: white;
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 12px;
                z-index: 2;
                white-space: nowrap;
            }
            .segment-secondary-highlight {
                position: relative;
                outline: 2px dotted #c0c0c0;
                margin-top: 36px !important;
            }
            .segment-secondary-highlight::before {
                content: "May be relevant";
                position: absolute;
                left: 0;
                top: 0;
                transform: translateY(-100%);
                background: #dedede;
                color: black;
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 12px;
                z-index: 2;
                white-space: nowrap;
            }
        `;
        document.head.appendChild(styleEl);
    }, []);

    // Scroll to segment and apply highlighting
    const scrollToElement = useCallback((element) => {
        if (!element || hasScrolledRef.current) return;

        hasScrolledRef.current = true;
        element.classList.add('segment-primary-highlight');

        // Use requestAnimationFrame to ensure layout is complete
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // Pin the selected segment to the top of the viewer (not centered) so the
                // user lands directly on it; the highlight class adds top margin for the
                // "Fragment you clicked" badge.
                element.scrollIntoView({behavior: 'smooth', block: 'start'});
            });
        });
    }, []);

    // Apply secondary highlights to search result segments
    const applySecondaryHighlights = useCallback(() => {
        if (!searchResultSegmentIds?.length || !segments?.length) return;

        const segmentIdSet = new Set(searchResultSegmentIds);

        segments.forEach((segmentData, index) => {
            const sid = segmentData?.segmentId;
            if (sid && segmentIdSet.has(sid) && index !== segmentIndex) {
                const element = document.getElementById(`segment-${index}`);
                if (element && !element.classList.contains('segment-primary-highlight')) {
                    element.classList.add('segment-secondary-highlight');
                }
            }
        });
    }, [searchResultSegmentIds, segments, segmentIndex]);

    // Main effect to scroll to segment
    useEffect(() => {
        // Reset scroll state when segment index changes
        hasScrolledRef.current = false;

        // Clean up previous highlights
        document.querySelectorAll('.segment-primary-highlight, .segment-secondary-highlight').forEach(el => {
            el.classList.remove('segment-primary-highlight', 'segment-secondary-highlight');
        });

        if (segmentIndex === null || Number.isNaN(segmentIndex)) {
            applySecondaryHighlights();
            return;
        }

        const targetId = `segment-${segmentIndex}`;

        // Try to find the element immediately
        const existingElement = document.getElementById(targetId);
        if (existingElement) {
            scrollToElement(existingElement);
            applySecondaryHighlights();
            return;
        }

        // Element doesn't exist yet, use MutationObserver to wait for it
        observerRef.current = new MutationObserver((mutations, obs) => {
            const element = document.getElementById(targetId);
            if (element) {
                obs.disconnect();
                scrollToElement(element);
                applySecondaryHighlights();
            }
        });

        observerRef.current.observe(document.body, {
            childList: true,
            subtree: true,
        });

        // Fallback timeout in case observer doesn't trigger
        const timeoutId = setTimeout(() => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            const element = document.getElementById(targetId);
            if (element) {
                scrollToElement(element);
            } else {
                console.warn('[useScrollToSegment] Could not find segment after 2s timeout:', targetId);
            }
            applySecondaryHighlights();
        }, 2000);

        return () => {
            clearTimeout(timeoutId);
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segmentIndex, scrollToElement, applySecondaryHighlights, segments.length]);

    // Cleanup highlights on unmount
    useEffect(() => {
        return () => {
            document.querySelectorAll('.segment-primary-highlight, .segment-secondary-highlight').forEach(el => {
                el.classList.remove('segment-primary-highlight', 'segment-secondary-highlight');
            });
        };
    }, []);
}
